/**
 * Story Edit - Tabbed Interface for Core + Language Translations
 *
 * Multi-language admin UI:
 * - "Details" tab for core story fields (slug, topic, brief, image)
 * - Language tabs for per-translation fields (title, script, phase, audio)
 */
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  useRedirect,
  useNotify,
  useRefresh,
  useGetList,
  Link,
} from 'react-admin';
import {
  Button,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  IconButton,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import { useParams, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Sparkles, Check, Ban } from 'lucide-react';
// Issue #640: Renamed from StoryPhase to StoryTransPhase to clarify scope
import {
  StoryTransPhase,
  STORY_TRANS_PHASE_CONFIG,
  type StoryPrePublishCheckId,
  type Language,
  logger,
  getMediaUrl,
  MediaConfig,
  getConventionalFilename,
  // Issue #851: regeneration wave planning from the pipeline step tables
  getRegenerationPlan,
  STORY_PIPELINE,
  STORY_TRANS_PIPELINE,
} from '@musedrops/shared';
import { handleEdgeFunctionResponse } from '../../../utils/response-handler';
import { supabase } from '../../../services/supabase';
import { ProcessLogSection } from '../../monitoring/components/ProcessLogSection';
import { DeliverySpecDisplay } from '../components/DeliverySpecDisplay';
import { FormTextField } from '../../../shared/components/forms';
import { AudioFileField } from '../../../shared/components/forms/AudioFileField';
import { FormPageLayout } from '../../../shared/components/forms/FormPageLayout';
import { useQueueJob } from '../../../hooks/useQueueJob';
import { TagsInput } from '../../../shared/components/TagsInput';
import { DebugBadge } from '../../../shared/components/DebugBadge';
import { CopyableId } from '../../../shared/components/CopyableId';
import { PrePublishSection } from '../components/PrePublishSection';
import { StoryAnnounceSection } from '../components/StoryAnnounceSection';
import { FreshnessBadge, stepLabel } from '../components/FreshnessBadge';
import { ImageGenerationSection } from '../../../shared/components/ImageGenerationSection';
import { EntityEnabledToggle } from '../../../shared/components/EntityEnabledToggle';
import { generateStoryImage } from '../../../services/imageService';
import { LanguageApi } from '../../../services/languageApi';
import {
  getStoryCore,
  getStoryTranslations,
  updateStoryCore,
  updateStoryTranslation,
} from '../../../data/story';

// Local type for translation without index signature (to avoid unknown type issues)
// StoryTranslation extends Record<string, unknown> which makes property access return unknown
type TranslationData = {
  storyId?: string;
  lang?: string;
  title?: string | null;
  subtitle?: string | null;
  description?: string | null;
  script?: string | null;
  searchTerms?: string | null;
  durationSec?: number | null;
  fileSizeBytes?: number | null;
  announceUpdatedAt?: string | null;
  voiceAudioUpdatedAt?: string | null;
  mixedAudioUpdatedAt?: string | null;
  externalAudioUpdatedAt?: string | null;
  phase?: string;
  error?: string | null;
  prePublishCheckedAt?: string | null;
  prePublishIssues?: StoryPrePublishCheckId[] | null;
  usedDebug?: boolean;
  // Issue #767: per-translation disabled state (terminal failure)
  enabled?: boolean | null;
  disabledAt?: string | null;
  disabledReason?: string | null;
  // Issue #851: freshness (cached by lifecycle-processor)
  frontStep?: string | null;
  releaseStatus?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

// Enriched similar story result (edge function data + secondary DB queries)
type SimilarStoryEnriched = {
  slug: string;
  title: string;
  similarity: number;
  showSlug?: string;
  showName?: string;
  clusterId?: number | null;
  imageUpdatedAt?: string | null;
};

interface StoryEditProps {
  mode?: 'create' | 'edit';
  initialShowSlug?: string;
}

// Tab panel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

export const StoryEdit = ({
  mode = 'edit',
  initialShowSlug,
}: StoryEditProps = {}) => {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const isCreateMode = mode === 'create' || id === 'create';
  const isEditMode = mode === 'edit' && id !== 'create';
  const actualId = isEditMode ? id : undefined;

  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  const [languages, setLanguages] = useState<Language[]>([]);

  // Core story state
  const [slug, setSlug] = useState('');
  const [topic, setTopic] = useState('');
  const [brief, setBrief] = useState('');
  const [takeaway, setTakeaway] = useState('');
  const [showSlug, setShowSlug] = useState('');
  const [tags, setTags] = useState<string | null>('');
  const [taxonomy, setTaxonomy] = useState<string | null>('');
  const [imagePrompt, setImagePrompt] = useState<string | null>('');
  const [imageUpdatedAt, setImageUpdatedAt] = useState<string | null>(null);
  // Issue #643: Story outline for script generation
  const [outline, setOutline] = useState<string | null>('');
  // #766 Phase 2/3: per-story delivery spec (read-only) + rolled form key
  const [deliverySpec, setDeliverySpec] = useState<unknown>(null);
  const [formKey, setFormKey] = useState<string | null>(null);
  const [ideaPresetUsed, setIdeaPresetUsed] = useState<string | null>(null);
  const [ideaUsedDebug, setIdeaUsedDebug] = useState(false); // Story-level debug (idea generation)
  const [publishedAt, setPublishedAt] = useState<string | null>(null);
  // Issue #646: Whether story has a generated embedding
  const [hasEmbedding, setHasEmbedding] = useState(false);
  // Issue #851: story-pipeline freshness front (read-only cache from #850a)
  const [storyFrontStep, setStoryFrontStep] = useState<string | null>(null);
  // Issue #767: Story-level enabled/disabled state (terminal failure or manual)
  const [coreEnabled, setCoreEnabled] = useState(true);
  const [coreDisabledReason, setCoreDisabledReason] = useState<string | null>(
    null
  );
  const [coreDisabledAt, setCoreDisabledAt] = useState<string | null>(null);
  const [coreBusy, setCoreBusy] = useState(false);
  // Issue #767: per-language enable/disable busy map (keyed by lang)
  const [translationDisableBusy, setTranslationDisableBusy] = useState<
    Record<string, boolean>
  >({});

  // Translation state (keyed by language)
  // Using TranslationData instead of Partial<StoryTranslation> to avoid index signature issues
  const [translations, setTranslations] = useState<
    Record<string, TranslationData>
  >({});

  // UI state
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Per-language processing states
  // Image and outline are story-level (not per-translation)
  const [isImageGenerating, setIsImageGenerating] = useState(false);
  // Similar stories state
  const [similarStories, setSimilarStories] = useState<
    SimilarStoryEnriched[]
  >([]);
  const [isSimilarLoading, setIsSimilarLoading] = useState(false);
  const [similarError, setSimilarError] = useState<string | null>(null);
  // Issue #643: Outline generation state
  const [isOutlineGenerating, setIsOutlineGenerating] = useState(false);
  // Issue #646: Embedding generation state
  const [isEmbeddingGenerating, setIsEmbeddingGenerating] = useState(false);
  // Translation processing keyed by lang, then by process type
  const [translationProcessing, setTranslationProcessing] = useState<
    Record<string, Record<string, boolean>>
  >({});

  // Track which process type + language is currently being watched
  // Issue #643: Added 'outline' for story outline generation
  const pendingJobRef = useRef<{
    type:
      | 'script'
      | 'tts'
      | 'mixingInternal'
      | 'mixingExternal'
      | 'image'
      | 'translation'
      | 'outline'
      | 'embedding';
    lang?: string;
  } | null>(null);
  const hasFetchedSimilar = useRef(false);

  // Get list of shows for display
  const { data: shows } = useGetList('shows', {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: 'slug', order: 'ASC' },
    filter: {},
  });

  // Get linked show
  const linkedShow = useMemo(
    () => shows?.find(show => show.slug === showSlug) ?? null,
    [shows, showSlug]
  );

  // Get global languages for tabs
  const globalLanguages = useMemo(() => {
    return languages.filter(lang => lang.includeInGlobalGen);
  }, [languages]);

  // Get applicable languages for this story
  const applicableLanguages = useMemo(() => {
    if (linkedShow?.lang) {
      // Single-language show - only show that language
      return globalLanguages.filter(l => l.key === linkedShow.lang);
    }
    // All-languages show
    return globalLanguages;
  }, [linkedShow, globalLanguages]);

  // Similar tab is placed after all language tabs
  const similarTabIndex = applicableLanguages.length + 1;

  // Current translation being edited
  const currentLang = useMemo(() => {
    if (activeTab === 0) return null; // Details tab
    const langIndex = activeTab - 1;
    return applicableLanguages[langIndex]?.key ?? null;
  }, [activeTab, applicableLanguages]);

  const currentTranslation = useMemo(() => {
    if (!currentLang) return null;
    return translations[currentLang] ?? {};
  }, [currentLang, translations]);

  const redirect = useRedirect();
  const notify = useNotify();
  const refresh = useRefresh();

  // Load languages
  useEffect(() => {
    const loadLanguages = async () => {
      try {
        const langs = await LanguageApi.getAll();
        setLanguages(langs);
      } catch (error) {
        logger.error('Failed to load languages', { error });
      }
    };
    loadLanguages();
  }, []);

  // Parse initial tab from URL
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && applicableLanguages.length > 0) {
      if (tabParam === 'details') {
        setActiveTab(0);
      } else if (tabParam === 'similar') {
        setActiveTab(applicableLanguages.length + 1);
      } else {
        // Find language tab
        const langIndex = applicableLanguages.findIndex(
          l => l.key === tabParam
        );
        if (langIndex >= 0) {
          setActiveTab(langIndex + 1);
        }
      }
    }
  }, [searchParams, applicableLanguages]);

  // Handle tab change
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    // Update URL
    if (newValue === 0) {
      setSearchParams({ tab: 'details' });
    } else if (newValue === similarTabIndex) {
      setSearchParams({ tab: 'similar' });
    } else {
      const lang = applicableLanguages[newValue - 1]?.key;
      if (lang) {
        setSearchParams({ tab: lang });
      }
    }
  };

  // Reset state when navigating to a different story
  useEffect(() => {
    setIsLoaded(false);
    setSimilarStories([]);
    setSimilarError(null);
    hasFetchedSimilar.current = false;
  }, [actualId]);

  // Load story data
  useEffect(() => {
    if (isCreateMode && !isLoaded) {
      setShowSlug(initialShowSlug || '');
      setIsLoaded(true);
      return;
    }

    if (!actualId || isLoaded) return;

    const loadData = async () => {
      try {
        // Load core story
        const core = await getStoryCore(actualId);
        if (!core) {
          notify('Story not found', { type: 'error' });
          redirect('list', 'stories');
          return;
        }

        setSlug(core.slug || '');
        setTopic(core.topic || '');
        setBrief(core.brief || '');
        setTakeaway(core.takeaway || '');
        setShowSlug(core.showSlug || '');
        setTags(core.tags || '');
        setTaxonomy(core.taxonomy || '');
        setImagePrompt(core.imagePrompt || '');
        setImageUpdatedAt(core.imageUpdatedAt || null);
        // Issue #643: Load story outline
        setOutline(core.outline || '');
        // #766 Phase 2/3: load delivery spec + rolled form (read-only display)
        setDeliverySpec((core as { deliverySpec?: unknown }).deliverySpec ?? null);
        setFormKey((core as { formKey?: string | null }).formKey ?? null);
        // Issue #646: Load embedding status
        setHasEmbedding(Boolean(core.hasEmbedding));
        setIdeaPresetUsed(core.ideaPresetUsed || null);
        setIdeaUsedDebug(Boolean(core.usedDebug)); // Story-level debug flag (idea generation)
        setPublishedAt(core.publishedAt || null);
        // Issue #851: freshness front cache
        setStoryFrontStep((core as { frontStep?: string | null }).frontStep ?? null);
        // Issue #767: Story-level enabled/disabled state
        setCoreEnabled(core.enabled !== false);
        setCoreDisabledReason(core.disabledReason ?? null);
        setCoreDisabledAt(core.disabledAt ?? null);

        // Load all translations (PK is story_slug + lang)
        const trans = await getStoryTranslations(core.slug);
        setTranslations(trans);

        setIsLoaded(true);
      } catch (error) {
        logger.error('Failed to load story', { error });
        notify('Failed to load story', { type: 'error' });
      }
    };

    loadData();
  }, [actualId, isCreateMode, isLoaded, initialShowSlug, notify, redirect]);

  // Update translation field
  const updateTranslationField = useCallback(
    (lang: string, field: keyof TranslationData, value: unknown) => {
      setTranslations(prev => ({
        ...prev,
        [lang]: {
          ...prev[lang],
          [field]: value,
        },
      }));
      setHasUnsavedChanges(true);
    },
    []
  );

  // Real-time subscription for story changes
  useEffect(() => {
    if (!actualId || isCreateMode) return undefined;

    const storiesChannel = supabase
      .channel(`story-${actualId}-core`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'stories',
          filter: `id=eq.${actualId}`,
        },
        payload => {
          const newRecord = payload.new as Record<string, unknown>;
          if (newRecord.image_updated_at !== undefined) {
            setImageUpdatedAt(newRecord.image_updated_at as string | null);
          }
          // Issue #767: Update story enabled/disabled from real-time subscription
          if (newRecord.enabled !== undefined) {
            setCoreEnabled(newRecord.enabled !== false);
          }
          if (newRecord.disabled_reason !== undefined) {
            setCoreDisabledReason(
              (newRecord.disabled_reason as string | null) ?? null
            );
          }
          if (newRecord.disabled_at !== undefined) {
            setCoreDisabledAt(
              (newRecord.disabled_at as string | null) ?? null
            );
          }
          // Issue #643: Update outline from real-time subscription
          if (newRecord.outline !== undefined) {
            setOutline((newRecord.outline as string) || '');
          }
          // #766: keep the delivery-spec display in sync after an outline
          // regeneration (callback rewrites delivery_spec + form_key alongside
          // the outline). Raw postgres_changes payload uses snake_case columns.
          if (newRecord.delivery_spec !== undefined) {
            setDeliverySpec(newRecord.delivery_spec ?? null);
          }
          if (newRecord.form_key !== undefined) {
            setFormKey((newRecord.form_key as string | null) ?? null);
          }
          // Issue #646: Update embedding status from real-time subscription
          if (newRecord.embedding !== undefined) {
            setHasEmbedding(
              newRecord.embedding != null && newRecord.embedding !== ''
            );
          }
          // Issue #851: freshness cache written by lifecycle-processor
          if (newRecord.front_step !== undefined) {
            setStoryFrontStep((newRecord.front_step as string | null) ?? null);
          }
        }
      )
      .subscribe();

    // Handler for translation changes (used for both INSERT and UPDATE)
    // Only update fields that are explicitly present in the payload to avoid
    // overwriting existing values with undefined when fields are missing
    const handleTranslationChange = (payload: {
      new: Record<string, unknown>;
    }) => {
      const newRecord = payload.new;
      const lang = newRecord.lang as string;
      if (lang) {
        setTranslations(prev => {
          const existing = prev[lang] || {};

          // Build updates object only with fields that are explicitly present in payload
          const updates: Partial<TranslationData> = {};

          // Content fields (generated by background processes)
          if ('title' in newRecord)
            updates.title = newRecord.title as string | null;
          if ('subtitle' in newRecord)
            updates.subtitle = newRecord.subtitle as string | null;
          if ('description' in newRecord)
            updates.description = newRecord.description as string | null;
          if ('search_terms' in newRecord)
            updates.searchTerms = newRecord.search_terms as string | null;
          if ('script' in newRecord)
            updates.script = newRecord.script as string | null;

          // Phase and audio fields (updated by lifecycle-processor and generation functions)
          if ('phase' in newRecord) updates.phase = newRecord.phase as string;
          if ('announce_updated_at' in newRecord) {
            updates.announceUpdatedAt = newRecord.announce_updated_at as
              | string
              | null;
          }
          if ('voice_audio_updated_at' in newRecord) {
            updates.voiceAudioUpdatedAt = newRecord.voice_audio_updated_at as
              | string
              | null;
          }
          if ('mixed_audio_updated_at' in newRecord) {
            updates.mixedAudioUpdatedAt = newRecord.mixed_audio_updated_at as
              | string
              | null;
          }
          if ('external_audio_updated_at' in newRecord) {
            updates.externalAudioUpdatedAt =
              newRecord.external_audio_updated_at as string | null;
          }
          if ('duration_sec' in newRecord)
            updates.durationSec = newRecord.duration_sec as number | null;
          if ('file_size_bytes' in newRecord)
            updates.fileSizeBytes = newRecord.file_size_bytes as number | null;
          if ('pre_publish_checked_at' in newRecord) {
            updates.prePublishCheckedAt = newRecord.pre_publish_checked_at as
              | string
              | null;
          }
          if ('pre_publish_issues' in newRecord) {
            updates.prePublishIssues = newRecord.pre_publish_issues as
              | StoryPrePublishCheckId[]
              | null;
          }
          // Issue #851: freshness cache columns written by lifecycle-processor —
          // the badges must track them live during regeneration waves
          if ('front_step' in newRecord) {
            updates.frontStep = newRecord.front_step as string | null;
          }
          if ('release_status' in newRecord) {
            updates.releaseStatus = newRecord.release_status as string | null;
          }

          return {
            ...prev,
            [lang]: {
              ...existing,
              ...updates,
            },
          };
        });
      }
    };

    // Filter by story_slug (PK is story_slug + lang)
    // Note: We need slug for this subscription, which is available after initial load
    const translationsChannel = supabase
      .channel(`story-${slug || actualId}-translations`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'story_translations',
          filter: `story_slug=eq.${slug}`,
        },
        payload => {
          handleTranslationChange(payload);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'story_translations',
          filter: `story_slug=eq.${slug}`,
        },
        payload => {
          handleTranslationChange(payload);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(storiesChannel);
      supabase.removeChannel(translationsChannel);
    };
  }, [actualId, isCreateMode, slug]);

  // Realtime (above) is the primary path for the freshness chips, but local
  // postgres_changes is unreliable, so the chips can sit stale during a
  // regeneration wave. Poll just the cheap freshness caches (front_step /
  // release_status) so they advance without a manual refresh. Content fields
  // are never touched, and state is only replaced when a value actually
  // changed — in-progress edits are safe and idle renders are avoided.
  useEffect(() => {
    if (!actualId || isCreateMode) return undefined;
    const poll = async () => {
      const { data: core } = await supabase
        .from('stories')
        .select('front_step')
        .eq('id', actualId)
        .maybeSingle();
      if (core) {
        setStoryFrontStep((core as { front_step?: string | null }).front_step ?? null);
      }
      if (!slug) return;
      const { data: rows } = await supabase
        .from('story_translations')
        .select('lang, front_step, release_status')
        .eq('story_slug', slug);
      if (!rows) return;
      setTranslations(prev => {
        let changed = false;
        const next = { ...prev };
        for (const row of rows as Array<{
          lang: string;
          front_step: string | null;
          release_status: string | null;
        }>) {
          const cur = next[row.lang];
          if (
            cur &&
            (cur.frontStep !== row.front_step ||
              cur.releaseStatus !== row.release_status)
          ) {
            next[row.lang] = {
              ...cur,
              frontStep: row.front_step,
              releaseStatus: row.release_status,
            };
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    };
    const interval = setInterval(poll, 4000);
    return () => clearInterval(interval);
  }, [actualId, isCreateMode, slug]);

  // Helper to clear processing state for a job
  const clearProcessingState = useCallback((type: string, lang?: string) => {
    if (type === 'image') {
      setIsImageGenerating(false);
    } else if (type === 'outline') {
      // Issue #643: Outline is story-level (not per-translation)
      setIsOutlineGenerating(false);
    } else if (type === 'embedding') {
      // Issue #646: Embedding is story-level
      setIsEmbeddingGenerating(false);
    } else if (lang) {
      setTranslationProcessing(prev => ({
        ...prev,
        [lang]: {
          ...prev[lang],
          [type]: false,
        },
      }));
    }
  }, []);

  // Handle generation job completion
  const handleJobComplete = useCallback(async () => {
    const pending = pendingJobRef.current;
    if (!pending) return;

    switch (pending.type) {
      case 'script':
        notify('Script generated successfully!');
        break;
      case 'tts':
        notify('TTS audio generated successfully!');
        break;
      case 'mixingInternal':
        notify('Internal audio generated successfully!');
        break;
      case 'mixingExternal':
        notify('External audio generated successfully!');
        break;
      case 'image':
        notify('Story image generated successfully!');
        break;
      case 'translation':
        notify('Translation generated successfully!');
        break;
      case 'outline':
        // Issue #643: Outline generation notification
        notify('Story outline generated successfully!');
        break;
      case 'embedding':
        // Issue #646: Embedding generation notification
        notify('Story embedding generated successfully!');
        setHasEmbedding(true);
        break;
      default:
        notify('Generation completed successfully!');
    }

    refresh();
    clearProcessingState(pending.type, pending.lang);
    pendingJobRef.current = null;
  }, [notify, refresh, clearProcessingState]);

  const handleJobError = useCallback(
    (error: string) => {
      const pending = pendingJobRef.current;
      if (!pending) return;
      notify(`${pending.type} generation failed: ${error}`, { type: 'error' });
      clearProcessingState(pending.type, pending.lang);
      pendingJobRef.current = null;
    },
    [notify, clearProcessingState]
  );

  const handleJobTimeout = useCallback(() => {
    const pending = pendingJobRef.current;
    if (!pending) return;
    notify(
      `${pending.type} generation timed out. Check the queue for status.`,
      {
        type: 'warning',
      }
    );
    clearProcessingState(pending.type, pending.lang);
    pendingJobRef.current = null;
  }, [notify, clearProcessingState]);

  const { watchJob } = useQueueJob({
    timeout: 5 * 60 * 1000,
    onComplete: handleJobComplete,
    onError: handleJobError,
    onTimeout: handleJobTimeout,
  });

  // Save handlers
  const handleSaveCore = async () => {
    if (!actualId) return;

    try {
      await updateStoryCore(actualId, {
        slug: slug.trim() || null,
        topic: topic.trim() || null,
        brief: brief.trim() || '',
        takeaway: takeaway.trim() || null,
        tags: tags || null,
        taxonomy: taxonomy || null,
        imagePrompt: imagePrompt?.trim() || null,
        outline: outline?.trim() || null, // Issue #643: Story outline
        // Issue #846: phase is derived by lifecycle-processor (single writer),
        // never written from the admin.
      });
      setHasUnsavedChanges(false);
      notify('Story details saved', { type: 'success' });
    } catch (error) {
      logger.error('Failed to save story core', { error });
      notify('Failed to save story details', { type: 'error' });
    }
  };

  const handleSaveTranslation = async () => {
    if (!slug || !currentLang || !currentTranslation) return;

    try {
      await updateStoryTranslation(slug, currentLang, {
        title: currentTranslation.title || '',
        subtitle: currentTranslation.subtitle || null,
        description: currentTranslation.description || null,
        script: currentTranslation.script || null,
        searchTerms: currentTranslation.searchTerms || null,
        // Issue #846: phase is derived by lifecycle-processor (single writer),
        // never written from the admin.
      });
      setHasUnsavedChanges(false);
      notify(`${currentLang.toUpperCase()} translation saved`, {
        type: 'success',
      });
    } catch (error) {
      logger.error('Failed to save translation', { error });
      notify('Failed to save translation', { type: 'error' });
    }
  };

  // Issue #767: per-translation disabled state (terminal failure recovery).
  // Returns true on success so callers can skip their optimistic state update.
  const handleReenable = async (lang: string): Promise<boolean> => {
    if (!actualId) return false;
    try {
      const { data, error } = await supabase.functions.invoke(
        'reenable-entity',
        {
          body: { entityType: 'story_trans', entityId: actualId, lang },
        }
      );
      if (error || !data?.success) {
        throw new Error(error?.message || data?.error || 'Re-enable failed');
      }
      notify(`Re-enabled ${lang}`, { type: 'success' });
      refresh();
      return true;
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Re-enable failed', {
        type: 'error',
      });
      return false;
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (activeTab === 0) {
        await handleSaveCore();
      } else if (currentLang) {
        await handleSaveTranslation();
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to leave?'
      );
      if (!confirmed) return;
    }
    redirect('list', 'stories');
  };

  // Helper to set processing state for a job
  const setProcessingState = useCallback(
    (type: string, lang: string | null, value: boolean) => {
      if (type === 'image') {
        setIsImageGenerating(value);
      } else if (type === 'outline') {
        // Issue #643: Outline is story-level (not per-translation)
        setIsOutlineGenerating(value);
      } else if (type === 'embedding') {
        // Issue #646: Embedding is story-level
        setIsEmbeddingGenerating(value);
      } else if (lang) {
        setTranslationProcessing(prev => ({
          ...prev,
          [lang]: {
            ...prev[lang],
            [type]: value,
          },
        }));
      }
    },
    []
  );

  // Manual generation handlers
  // Issue #643: Added 'outline' for story outline generation
  // Issue #851: pipeline step behind each manual-generation action (steps only —
  // 'translation' is not a pipeline step and regenerates in place)
  const PIPELINE_STEP_FOR_PROCESS: Record<string, string> = {
    outline: 'gen-story-outline',
    embedding: 'gen-story-embedding',
    image: 'gen-story-image',
    script: 'gen-story-script',
    tts: 'gen-story-voice',
    mixingInternal: 'gen-story-mixed-audio',
    mixingExternal: 'gen-story-mixed-audio-external',
  };

  const snakeToCamel = (field: string) =>
    field.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());

  // Sync local component state after clearing story-level artifacts.
  const clearStoryLocalState = (clearedKeys: Record<string, unknown>) => {
    if ('outline' in clearedKeys) setOutline('');
    if ('deliverySpec' in clearedKeys) setDeliverySpec(null);
    if ('embedding' in clearedKeys) setHasEmbedding(false);
    if ('imageUpdatedAt' in clearedKeys) setImageUpdatedAt(null);
  };

  /**
   * Regenerate a pipeline step (Issue #846). Staleness is permanent for
   * story / story_trans, so this is always zero-downtime: clear ONLY the
   * target step's artifacts and leave the phase untouched. The engine re-runs
   * that step and rebuilds exactly the downstream steps that depend on it —
   * no phase reset, and a published story stays live/serving throughout.
   *
   * Returns false when the user cancels.
   */
  const runRegenerationWave = async (
    stepKey: string,
    langKey?: string | null
  ): Promise<boolean> => {
    if (!actualId) return false;
    const isStoryStep = STORY_PIPELINE.steps.some(s => s.key === stepKey);
    const config = isStoryStep ? STORY_PIPELINE : STORY_TRANS_PIPELINE;
    const plan = getRegenerationPlan(config, stepKey);

    if (!window.confirm(
      `Regenerate ${stepLabel(stepKey)}?\n\nIt rebuilds live: dependent ` +
      'downstream artifacts refresh in the background, and a published ' +
      'story stays online the whole time.'
    )) return false;

    const updates: Record<string, unknown> = Object.fromEntries(
      plan.step.produces.map(f => [snakeToCamel(f), null])
    );
    // NOTE: no `updates.phase` — the engine keeps the phase where it is.

    if (isStoryStep) {
      await updateStoryCore(actualId, updates);
      clearStoryLocalState(updates);
    } else {
      if (!langKey || !slug) return false;
      await updateStoryTranslation(slug, langKey, updates);
      setTranslations(await getStoryTranslations(slug));
    }
    return true;
  };

  const handleManualGeneration = async (
    processType:
      | 'script'
      | 'tts'
      | 'mixingInternal'
      | 'mixingExternal'
      | 'image'
      | 'translation'
      | 'outline'
      | 'embedding',
    langKey?: string
  ) => {
    if (!actualId) return;

    // Image, outline, and embedding generation are story-level (not per-translation), don't need lang
    const effectiveLang = langKey || currentLang;
    if (
      processType !== 'image' &&
      processType !== 'outline' &&
      processType !== 'embedding' &&
      !effectiveLang
    )
      return;

    setProcessingState(processType, effectiveLang, true);

    try {
      let functionName: string;

      switch (processType) {
        case 'translation':
          functionName = 'gen-story-translation';
          break;
        case 'script':
          functionName = 'gen-story-script';
          break;
        // Issue #643: Story outline generation (story-level, no lang needed)
        case 'outline':
          functionName = 'gen-story-outline';
          break;
        // Issue #646: Story embedding generation (story-level, no lang needed)
        case 'embedding':
          functionName = 'gen-story-embedding';
          break;
        case 'image':
          try {
            // Issue #851: image is the last story step — wave is a no-op
            // downstream, but the confirm + phase reset keep semantics uniform
            if (!(await runRegenerationWave('gen-story-image'))) {
              setProcessingState(processType, null, false);
              return;
            }
            const result = await generateStoryImage({ storyId: actualId });
            if (result.success && result.jobId) {
              pendingJobRef.current = { type: processType };
              watchJob(result.jobId);
            } else {
              notify(result.error || 'Failed to start image generation', {
                type: 'error',
              });
              setProcessingState(processType, null, false);
            }
          } catch (err) {
            notify(
              err instanceof Error ? err.message : 'Unknown error occurred',
              { type: 'error' }
            );
            setProcessingState(processType, null, false);
          }
          return;
        case 'tts':
          if (hasUnsavedChanges) {
            const confirmed = window.confirm(
              'Script changes need to be saved first. Would you like to save now?'
            );
            if (confirmed) {
              await handleSaveTranslation();
            } else {
              setProcessingState(processType, effectiveLang, false);
              return;
            }
          }
          functionName = 'gen-story-voice';
          break;
        case 'mixingInternal':
        case 'mixingExternal':
          if (hasUnsavedChanges) {
            const confirmed = window.confirm(
              'Changes need to be saved first. Would you like to save now?'
            );
            if (confirmed) {
              await handleSaveTranslation();
            } else {
              setProcessingState(processType, effectiveLang, false);
              return;
            }
          }
          functionName = 'gen-story-mixed-audio';
          break;
        default:
          throw new Error(`Unknown process type: ${processType}`);
      }

      // For mixing, add target based on process type
      const mixingTarget =
        processType === 'mixingInternal'
          ? 'internal'
          : processType === 'mixingExternal'
            ? 'external'
            : undefined;
      // Issue #643/#646: Outline and embedding (like image) are story-level, don't need lang
      const functionPayload: Record<string, unknown> = {
        storyId: actualId,
        ...(processType !== 'outline' &&
          processType !== 'embedding' && { lang: effectiveLang }),
        trigger: 'manual', // UI-initiated jobs get priority
        ...(mixingTarget && { target: mixingTarget }),
      };

      if (processType === 'tts' && !currentTranslation?.script) {
        notify('Script is required for TTS generation', { type: 'error' });
        setProcessingState(processType, effectiveLang, false);
        return;
      }

      if (
        (processType === 'mixingInternal' ||
          processType === 'mixingExternal') &&
        !currentTranslation?.voiceAudioUpdatedAt
      ) {
        notify('Voice audio is required for mixing', { type: 'error' });
        setProcessingState(processType, effectiveLang, false);
        return;
      }

      // Issue #851: regeneration wave — clear downstream + reset phase first,
      // so the pipeline rebuilds the chain in order (after all validations,
      // so a failed validation never clears anything).
      const waveStep = PIPELINE_STEP_FOR_PROCESS[processType];
      if (waveStep && !(await runRegenerationWave(waveStep, effectiveLang))) {
        setProcessingState(processType, effectiveLang, false);
        return;
      }

      const { data, error } = await supabase.functions.invoke(functionName, {
        body: functionPayload,
      });

      if (error) {
        notify(`Failed to generate ${processType}`, { type: 'error' });
        setProcessingState(processType, effectiveLang, false);
        return;
      }

      const result = handleEdgeFunctionResponse(data, notify);

      // All processes return single jobId (mixing is sequential: internal first, then lifecycle triggers external)
      if (result && typeof result === 'object' && 'jobId' in result) {
        pendingJobRef.current = {
          type: processType,
          lang: effectiveLang ?? undefined,
        };
        watchJob(result.jobId as number);
      } else {
        notify(`${processType} generation failed to queue`, { type: 'error' });
        setProcessingState(processType, effectiveLang, false);
      }
    } catch (err) {
      logger.error(`${processType} generation failed`, {
        error: err instanceof Error ? err.message : String(err),
      });
      notify(`Failed to generate ${processType}`, { type: 'error' });
      setProcessingState(processType, effectiveLang, false);
    }
  };

  // Fetch similar stories using vector similarity + enrich with DB data
  const fetchSimilarStories = useCallback(async () => {
    if (!slug) return;

    setIsSimilarLoading(true);
    setSimilarError(null);
    setSimilarStories([]);

    try {
      // 1. Call existing edge function with fixed params per issue #663
      const { data, error } = await supabase.functions.invoke(
        'similar-stories',
        {
          body: {
            slug,
            lang: 'en',
            limit: 20,
            minSimilarity: 0.3,
            maxPerShow: 10,
          },
        }
      );

      if (error) throw new Error(error.message);
      if (!data?.success || !data?.results) {
        throw new Error(data?.error || 'Unknown error');
      }

      const { results }: {
        results: Array<{
          slug: string;
          title: string;
          similarity: number;
        }>;
      } = data;

      if (results.length === 0) return;

      // 2. Enrich with story data (cluster_id, image_updated_at, show_slug)
      const slugs = results.map(r => r.slug);
      const { data: storyData } = await supabase
        .from('stories')
        .select('slug, show_slug, cluster_id, image_updated_at')
        .in('slug', slugs);

      // 3. Enrich with show names (en)
      const showSlugs = [
        ...new Set(
          (storyData || []).map(s => s.show_slug).filter(Boolean)
        ),
      ];
      const { data: showData } =
        showSlugs.length > 0
          ? await supabase
              .from('show_translations')
              .select('show_slug, name')
              .in('show_slug', showSlugs)
              .eq('lang', 'en')
          : { data: [] };

      // 4. Build lookup maps and merge
      const storyMap = new Map(
        (storyData || []).map(s => [s.slug, s])
      );
      const showMap = new Map(
        (showData || []).map(s => [s.show_slug, s.name])
      );

      setSimilarStories(
        results.map(r => {
          const story = storyMap.get(r.slug);
          return {
            slug: r.slug,
            title: r.title,
            similarity: r.similarity,
            showSlug: story?.show_slug,
            showName: story?.show_slug
              ? showMap.get(story.show_slug) || story.show_slug
              : undefined,
            clusterId: story?.cluster_id,
            imageUpdatedAt: story?.image_updated_at,
          };
        })
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to find similar stories';
      setSimilarError(errorMessage);
      logger.error('Failed to find similar stories', { error: err });
    } finally {
      setIsSimilarLoading(false);
    }
  }, [slug]);

  // Auto-fetch similar stories when tab is first activated
  useEffect(() => {
    if (
      activeTab === similarTabIndex &&
      slug &&
      !hasFetchedSimilar.current &&
      !isSimilarLoading
    ) {
      hasFetchedSimilar.current = true;
      fetchSimilarStories();
    }
  }, [activeTab, similarTabIndex, slug, isSimilarLoading, fetchSimilarStories]);

  // Construct audio URLs for current translation
  const voiceAudioUrl = useMemo(() => {
    if (!slug || !currentTranslation?.voiceAudioUpdatedAt || !currentLang)
      return undefined;
    const filename = getConventionalFilename(
      'story',
      'voiceAudio',
      currentLang
    );
    return `${getMediaUrl('stories', slug, filename)}?t=${new Date(currentTranslation.voiceAudioUpdatedAt).getTime()}`;
  }, [slug, currentTranslation?.voiceAudioUpdatedAt, currentLang]);

  const mixedAudioUrl = useMemo(() => {
    if (!slug || !currentTranslation?.mixedAudioUpdatedAt || !currentLang)
      return undefined;
    const filename = getConventionalFilename(
      'story',
      'mixedAudio',
      currentLang
    );
    return `${getMediaUrl('stories', slug, filename)}?t=${new Date(currentTranslation.mixedAudioUpdatedAt).getTime()}`;
  }, [slug, currentTranslation?.mixedAudioUpdatedAt, currentLang]);

  const externalAudioUrl = useMemo(() => {
    if (!slug || !currentTranslation?.externalAudioUpdatedAt || !currentLang)
      return undefined;
    const filename = getConventionalFilename(
      'story',
      'externalAudio',
      currentLang
    );
    return `${getMediaUrl('stories', slug, filename)}?t=${new Date(currentTranslation.externalAudioUpdatedAt).getTime()}`;
  }, [slug, currentTranslation?.externalAudioUpdatedAt, currentLang]);

  // Loading state
  if (isEditMode && !isLoaded) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <FormPageLayout
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isCreateMode ? 'Create New Story' : topic || 'Edit Story'}
          {isEditMode && <DebugBadge show={ideaUsedDebug} size={20} />}
        </Box>
      }
      onCancel={handleCancel}
      onSave={handleSave}
      saveButtonText={activeTab === 0 ? 'Save Details' : 'Save Translation'}
      isFormValid={activeTab === 0 ? brief.trim().length > 0 : !!currentLang}
      hasUnsavedChanges={hasUnsavedChanges}
      isSaving={isSaving}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* Metadata row: ID + Show link */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 0.5 }}>
          {isEditMode && actualId && (
            <CopyableId id={actualId} variant="caption" />
          )}
          <Typography variant="caption" color="text.secondary">
            Show:{' '}
            {linkedShow ? (
              <Link to={`/shows/${linkedShow.id}/edit`}>{showSlug}</Link>
            ) : (
              showSlug || '—'
            )}
          </Typography>
          {ideaPresetUsed && (
            <Typography variant="caption" color="text.secondary">
              Idea: {ideaPresetUsed}
            </Typography>
          )}
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Details" />
            {applicableLanguages.map(lang => {
              const trans = translations[lang.key];
              const phase = trans?.phase as StoryTransPhase | undefined;
              const config = phase ? STORY_TRANS_PHASE_CONFIG[phase] : null;
              return (
                <Tab
                  key={lang.key}
                  label={
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      {lang.key.toUpperCase()}
                      {config?.emoji && (
                        <span style={{ fontSize: '1rem' }}>{config.emoji}</span>
                      )}
                      {Boolean(trans?.usedDebug) && (
                        <DebugBadge show size={12} />
                      )}
                    </Box>
                  }
                />
              );
            })}
            {isEditMode && <Tab label="Similar" />}
          </Tabs>
        </Box>

        {/* Details Tab */}
        <TabPanel value={activeTab} index={0}>
          {/* Issue #846: story phase is derived by lifecycle-processor (single
              writer) — there is no manual override. Status shows via the
              FreshnessBadge beside the enabled switch. */}
          {/* Issue #767: Story-level Enabled toggle as its own full-width row */}
          {isEditMode && (
            <Box sx={{ mb: 2 }}>
              {/* Issue #851: freshness indicator sits beside the enabled switch */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <EntityEnabledToggle
                enabled={coreEnabled}
                disabledReason={coreDisabledReason}
                disabledAt={coreDisabledAt}
                busy={coreBusy}
                sx={{ width: 'auto', flex: '0 0 auto' }}
                onToggle={async (next, reason) => {
                  if (!actualId) return;
                  setCoreBusy(true);
                  const disabledAt = new Date().toISOString();
                  const disabledReason = reason || 'Manually disabled';
                  try {
                    if (next) {
                      const { data, error } =
                        await supabase.functions.invoke('reenable-entity', {
                          body: { entityType: 'story', entityId: actualId },
                        });
                      if (error || !data?.success) {
                        throw new Error(
                          error?.message || data?.error || 'Re-enable failed'
                        );
                      }
                    } else {
                      await updateStoryCore(actualId, {
                        enabled: false,
                        disabledAt,
                        disabledReason,
                      });
                    }
                    setCoreEnabled(next);
                    setCoreDisabledReason(next ? null : disabledReason);
                    setCoreDisabledAt(next ? null : disabledAt);
                    notify(next ? 'Story enabled' : 'Story disabled', {
                      type: 'success',
                    });
                    refresh();
                  } catch (err) {
                    notify(
                      err instanceof Error ? err.message : 'Update failed',
                      { type: 'error' }
                    );
                  } finally {
                    setCoreBusy(false);
                  }
                }}
              />
              <FreshnessBadge frontStep={storyFrontStep} hideRelease />
              </Box>
            </Box>
          )}

          {/* Slug */}
          <FormTextField
            label="Slug (ID)"
            value={slug}
            onChange={e => {
              setSlug(e.target.value);
              setHasUnsavedChanges(true);
            }}
            width="full"
          />

          {/* Topic */}
          <FormTextField
            label="Topic"
            value={topic}
            onChange={e => {
              setTopic(e.target.value);
              setHasUnsavedChanges(true);
            }}
            width="full"
          />

          {/* Brief */}
          <FormTextField
            label="Brief (for LLM)"
            value={brief}
            onChange={e => {
              setBrief(e.target.value);
              setHasUnsavedChanges(true);
            }}
            required
            rows={3}
            showCount={false}
          />

          {/* Takeaway */}
          <FormTextField
            label="Takeaway"
            value={takeaway}
            onChange={e => {
              setTakeaway(e.target.value);
              setHasUnsavedChanges(true);
            }}
            rows={2}
            showCount={false}
          />

          {/* Tags */}
          <TagsInput
            value={tags}
            onChange={val => {
              setTags(val);
              setHasUnsavedChanges(true);
            }}
            label="Tags"
            fullWidth
          />

          {/* Taxonomy */}
          <FormTextField
            label="Taxonomy"
            value={taxonomy || ''}
            onChange={e => {
              setTaxonomy(e.target.value);
              setHasUnsavedChanges(true);
            }}
          />

          {/* Issue #643: Story Outline Section */}
          {isEditMode && (
            <FormTextField
              label="Story Outline"
              value={outline || ''}
              onChange={e => {
                setOutline(e.target.value);
                setHasUnsavedChanges(true);
              }}
              rows={5}
              placeholder="Story structure outline (auto-generated from brief)"
              onRegenerate={() => handleManualGeneration('outline')}
              isRegenerating={isOutlineGenerating}
              regenerateTitle={
                !brief
                  ? 'Brief is required to generate outline'
                  : 'Generate outline using AI'
              }
              regenerateDisabled={!brief}
            />
          )}

          {/* #766 Phase 2/3: Delivery spec (read-only, generated with the outline) */}
          {isEditMode && (
            <DeliverySpecDisplay spec={deliverySpec as never} formKey={formKey} />
          )}

          {/* Issue #646: Embedding Status */}
          {isEditMode && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mt: 1,
                mb: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Embedding:
              </Typography>
              {hasEmbedding ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Check size={16} color="#4caf50" />
                  <Typography variant="body2" color="success.main">
                    Created
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Ban size={16} color="#9e9e9e" />
                  <Typography variant="body2" color="text.disabled">
                    Missing
                  </Typography>
                </Box>
              )}
              <IconButton
                size="small"
                onClick={() => handleManualGeneration('embedding')}
                disabled={isEmbeddingGenerating || !brief}
                title={
                  !brief
                    ? 'Brief is required to generate embedding'
                    : 'Generate embedding using AI'
                }
              >
                {isEmbeddingGenerating ? (
                  <CircularProgress size={16} />
                ) : (
                  <Sparkles size={16} />
                )}
              </IconButton>
            </Box>
          )}

          {/* Image Section */}
          {isEditMode && linkedShow && (
            <Box sx={{ mt: 3 }}>
              <ImageGenerationSection
                entityType="story"
                entityId={actualId}
                entitySlug={slug}
                entityName={topic}
                showTopic={linkedShow.topic}
                languageKey={linkedShow.lang}
                imagePrompt={imagePrompt}
                imageUpdatedAt={imageUpdatedAt}
                onImagePromptChange={value => {
                  setImagePrompt(value);
                  setHasUnsavedChanges(true);
                }}
                onImageUpdatedAtChange={setImageUpdatedAt}
                onGenerate={() => handleManualGeneration('image')}
                isGenerating={isImageGenerating}
                generationError={null}
                allowStoryImages={linkedShow.allowStoryImages}
              />
            </Box>
          )}

        </TabPanel>

        {/* Language Tabs */}
        {applicableLanguages.map((lang, index) => {
          const trans: TranslationData = translations[lang.key] || {};
          const tabIndex = index + 1;

          return (
            <TabPanel key={lang.key} value={activeTab} index={tabIndex}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                }}
              >
                <Typography variant="h6">{lang.name}</Typography>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() =>
                      handleManualGeneration('translation', lang.key)
                    }
                    disabled={!!translationProcessing[lang.key]?.translation}
                    startIcon={
                      translationProcessing[lang.key]?.translation ? (
                        <CircularProgress size={14} />
                      ) : (
                        <Sparkles size={14} />
                      )
                    }
                  >
                    {translationProcessing[lang.key]?.translation
                      ? 'Generating...'
                      : 'Generate'}
                  </Button>
                </Box>
              </Box>

              {/* Issue #767 toggle + Issue #851 freshness indicator in one row */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1 }}>
              <EntityEnabledToggle
                enabled={trans.enabled !== false}
                disabledReason={trans.disabledReason}
                disabledAt={trans.disabledAt}
                busy={!!translationDisableBusy[lang.key]}
                sx={{ width: 'auto', flex: '0 0 auto' }}
                onToggle={async (next, reason) => {
                  setTranslationDisableBusy(prev => ({
                    ...prev,
                    [lang.key]: true,
                  }));
                  const disabledAt = new Date().toISOString();
                  const disabledReason = reason || 'Manually disabled';
                  try {
                    if (next) {
                      // handleReenable invokes reenable-entity and notifies/refreshes;
                      // it never throws — on failure, keep showing the disabled state.
                      const ok = await handleReenable(lang.key);
                      if (!ok) return;
                    } else {
                      await updateStoryTranslation(slug, lang.key, {
                        enabled: false,
                        disabledAt,
                        disabledReason,
                      });
                      notify(`Disabled ${lang.key}`, { type: 'success' });
                    }
                    // Reflect the new state immediately: refresh() does not re-fetch
                    // the locally-loaded translations, so update the state directly.
                    setTranslations(prev => ({
                      ...prev,
                      [lang.key]: {
                        ...prev[lang.key],
                        enabled: next,
                        disabledReason: next ? null : disabledReason,
                        disabledAt: next ? null : disabledAt,
                      },
                    }));
                  } catch (err) {
                    notify(
                      err instanceof Error ? err.message : 'Update failed',
                      { type: 'error' }
                    );
                  } finally {
                    setTranslationDisableBusy(prev => ({
                      ...prev,
                      [lang.key]: false,
                    }));
                  }
                }}
              />
              <FreshnessBadge
                frontStep={trans?.frontStep}
                releaseStatus={trans?.releaseStatus}
              />
              </Box>

              {/* Published Warning */}
              {trans.phase === 'PUBLISHED' && (
                <Paper
                  sx={{
                    p: 2,
                    mb: 2,
                    bgcolor: 'warning.light',
                    border: '1px solid',
                    borderColor: 'warning.main',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AlertTriangle size={20} />
                    <Typography variant="body2">
                      <strong>Published:</strong> Changes may affect live
                      content.
                    </Typography>
                  </Box>
                </Paper>
              )}

              {/* Pre-Publish Section */}
              {isEditMode && actualId && (
                <PrePublishSection
                  storyId={actualId}
                  lang={lang.key}
                  phase={trans.phase as StoryTransPhase}
                  mixedAudioUpdatedAt={
                    trans.mixedAudioUpdatedAt as string | null
                  }
                  prePublishCheckedAt={
                    trans.prePublishCheckedAt as string | null
                  }
                  prePublishIssues={
                    trans.prePublishIssues as StoryPrePublishCheckId[] | null
                  }
                />
              )}

              {/* Title */}
              <FormTextField
                label="Title"
                value={String(trans.title ?? '')}
                onChange={e =>
                  updateTranslationField(lang.key, 'title', e.target.value)
                }
                width="full"
                required
              />

              {/* Subtitle */}
              <FormTextField
                label="Subtitle"
                value={String(trans.subtitle ?? '')}
                onChange={e =>
                  updateTranslationField(lang.key, 'subtitle', e.target.value)
                }
                width="full"
              />

              {/* Description */}
              <FormTextField
                label="Description"
                value={String(trans.description ?? '')}
                onChange={e =>
                  updateTranslationField(
                    lang.key,
                    'description',
                    e.target.value
                  )
                }
                rows={2}
                showCount={false}
              />

              {/* Search Terms */}
              <FormTextField
                label="Search terms"
                value={String(trans.searchTerms ?? '')}
                onChange={e =>
                  updateTranslationField(
                    lang.key,
                    'searchTerms',
                    e.target.value
                  )
                }
              />

              {/* Issue #846: translation phase is derived by lifecycle-processor
                  (single writer) — no manual override. Status shows via the
                  FreshnessBadge above. */}

              {/* Published At (story-level, shown in translation tab for context) */}
              {publishedAt && (
                <Box
                  sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Published: {new Date(String(publishedAt)).toLocaleString()}
                  </Typography>
                </Box>
              )}

              {/* Script */}
              <FormTextField
                label="Script"
                value={String(trans.script ?? '')}
                onChange={e =>
                  updateTranslationField(lang.key, 'script', e.target.value)
                }
                rows={15}
                showCount
                helperText={(() => {
                  const script = trans.script ?? '';
                  const wordCount = script
                    .split(/\s+/)
                    .filter(w => w.length > 0).length;
                  const byteCount = new TextEncoder().encode(script).length;
                  return `${wordCount} words ${byteCount} bytes`;
                })()}
                onRegenerate={() => handleManualGeneration('script', lang.key)}
                isRegenerating={!!translationProcessing[lang.key]?.script}
                regenerateTitle="Generate script using AI"
              />

              {/* Announcement Audio */}
              <StoryAnnounceSection
                storySlug={slug}
                storyId={actualId}
                lang={lang.key}
                announceUpdatedAt={trans.announceUpdatedAt}
                onAnnounceDeleted={() =>
                  updateTranslationField(lang.key, 'announceUpdatedAt', null)
                }
                onAnnounceUpdatedAtChanged={timestamp =>
                  updateTranslationField(
                    lang.key,
                    'announceUpdatedAt',
                    timestamp
                  )
                }
                onBeforeRegenerate={() =>
                  runRegenerationWave('gen-story-announce', lang.key)
                }
              />

              {/* Voice Audio */}
              <AudioFileField
                label="Voice Audio"
                value={
                  trans.voiceAudioUpdatedAt
                    ? MediaConfig.story.voiceAudio
                    : null
                }
                audioUrl={voiceAudioUrl}
                onTimestampChanged={timestamp =>
                  updateTranslationField(
                    lang.key,
                    'voiceAudioUpdatedAt',
                    timestamp
                  )
                }
                onDelete={() =>
                  updateTranslationField(lang.key, 'voiceAudioUpdatedAt', null)
                }
                onRegenerate={() => handleManualGeneration('tts', lang.key)}
                isRegenerating={!!translationProcessing[lang.key]?.tts}
                regenerateTitle="Generate TTS audio from script"
                RegenerateIcon={
                  Sparkles as React.ComponentType<{ size?: number }>
                }
                storageFolder="stories"
                slug={slug}
                entityId={slug}
                tableName="story_translations"
                fileType="voiceAudio"
                lang={lang.key}
                accept="audio/*"
                showUpload={false}
                showGenerate
              />

              {/* Mixed Audio Fields */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                  <AudioFileField
                    label="Mixed Audio (internal)"
                    value={
                      trans.mixedAudioUpdatedAt
                        ? MediaConfig.story.mixedAudio
                        : null
                    }
                    audioUrl={mixedAudioUrl}
                    onTimestampChanged={timestamp =>
                      updateTranslationField(
                        lang.key,
                        'mixedAudioUpdatedAt',
                        timestamp
                      )
                    }
                    onDelete={() =>
                      updateTranslationField(
                        lang.key,
                        'mixedAudioUpdatedAt',
                        null
                      )
                    }
                    onRegenerate={() =>
                      handleManualGeneration('mixingInternal', lang.key)
                    }
                    isRegenerating={
                      !!translationProcessing[lang.key]?.mixingInternal
                    }
                    regenerateTitle="Mix voice audio with show audio"
                    RegenerateIcon={
                      Sparkles as React.ComponentType<{ size?: number }>
                    }
                    storageFolder="stories"
                    slug={slug}
                    entityId={slug}
                    tableName="story_translations"
                    fileType="mixedAudio"
                    lang={lang.key}
                    accept="audio/*"
                    showUpload={false}
                    showGenerate
                    helperText={
                      trans.durationSec || trans.fileSizeBytes
                        ? `${trans.durationSec ? `Duration: ${Math.floor((trans.durationSec as number) / 60)}:${String((trans.durationSec as number) % 60).padStart(2, '0')}` : ''}${trans.durationSec && trans.fileSizeBytes ? ' · ' : ''}${trans.fileSizeBytes ? `Size: ${((trans.fileSizeBytes as number) / (1024 * 1024)).toFixed(1)} MB` : ''}`
                        : undefined
                    }
                  />
                </Box>

                <Box sx={{ flex: 1 }}>
                  <AudioFileField
                    label="External Audio (with signature)"
                    value={
                      trans.externalAudioUpdatedAt
                        ? MediaConfig.story.externalAudio
                        : null
                    }
                    audioUrl={externalAudioUrl}
                    onTimestampChanged={timestamp =>
                      updateTranslationField(
                        lang.key,
                        'externalAudioUpdatedAt',
                        timestamp
                      )
                    }
                    onDelete={() =>
                      updateTranslationField(
                        lang.key,
                        'externalAudioUpdatedAt',
                        null
                      )
                    }
                    onRegenerate={() =>
                      handleManualGeneration('mixingExternal', lang.key)
                    }
                    isRegenerating={
                      !!translationProcessing[lang.key]?.mixingExternal
                    }
                    regenerateTitle="Mix voice audio with signature"
                    RegenerateIcon={
                      Sparkles as React.ComponentType<{ size?: number }>
                    }
                    storageFolder="stories"
                    slug={slug}
                    entityId={slug}
                    tableName="story_translations"
                    fileType="externalAudio"
                    lang={lang.key}
                    accept="audio/*"
                    showUpload={false}
                    showGenerate
                  />
                </Box>
              </Box>

              {/* Debug Flag */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(trans.usedDebug)}
                    onChange={e =>
                      updateTranslationField(
                        lang.key,
                        'usedDebug',
                        e.target.checked
                      )
                    }
                    sx={{
                      color: trans.usedDebug ? '#f44336' : undefined,
                      '&.Mui-checked': { color: '#f44336' },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: trans.usedDebug ? '#f44336' : 'text.secondary',
                      }}
                    >
                      Generated with debug model preset
                    </Typography>
                    <DebugBadge
                      show={Boolean(trans.usedDebug)}
                      size={14}
                      showTooltip={false}
                    />
                  </Box>
                }
              />
            </TabPanel>
          );
        })}

        {/* Similar Stories Tab */}
        {isEditMode && (
          <TabPanel value={activeTab} index={similarTabIndex}>
            {isSimilarLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {similarError && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                {similarError}
              </Typography>
            )}

            {!isSimilarLoading &&
              !similarError &&
              similarStories.length === 0 &&
              hasFetchedSimilar.current && (
                <Typography variant="body2" color="text.secondary">
                  No similar stories found.
                </Typography>
              )}

            {similarStories.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {similarStories.map((story, index) => (
                  <Box
                    key={story.slug}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      py: 0.5,
                      px: 1,
                      borderRadius: 1,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    {/* Rank */}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ minWidth: 20, textAlign: 'right' }}
                    >
                      {index + 1}
                    </Typography>

                    {/* Thumbnail */}
                    <Box
                      component="img"
                      src={`${getMediaUrl('stories', story.slug, 'default.png')}${story.imageUpdatedAt ? `?t=${new Date(story.imageUpdatedAt).getTime()}` : ''}`}
                      sx={{
                        width: 70,
                        height: 70,
                        objectFit: 'cover',
                        borderRadius: 1,
                        bgcolor: 'grey.200',
                        flexShrink: 0,
                      }}
                      onError={(
                        e: React.SyntheticEvent<HTMLImageElement>
                      ) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />

                    {/* Show name + Story name */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                      >
                        {story.showName || story.showSlug || '—'}
                      </Typography>
                      <Link to={`/stories/${story.slug}/edit`}>
                        <Typography variant="body2" noWrap>
                          {story.title || story.slug}
                        </Typography>
                      </Link>
                    </Box>

                    {/* Similarity */}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ minWidth: 45, textAlign: 'right' }}
                    >
                      {story.similarity.toFixed(3)}
                    </Typography>

                    {/* Cluster ID */}
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ minWidth: 30, textAlign: 'right' }}
                    >
                      {story.clusterId != null
                        ? `C${story.clusterId}`
                        : '—'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </TabPanel>
        )}

        {/* Process Logs */}
        {isEditMode && actualId && activeTab === 0 && (
          <ProcessLogSection
            entityType="story"
            entityId={actualId}
            entityTitle={topic}
          />
        )}
      </Box>
    </FormPageLayout>
  );
};
